import React, { useEffect } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import MultiSelect from '../components/MultiSelect';
import { FormField, FieldType, DataSource, DataSourceParam } from '../types';
import { dangerIconButton, inputClass, labelClass, subtleButton, helpClass } from '../ui';
import { Field } from './parts';

export function defaultDataSource(field: FormField): DataSource {
  return {
    request: {
      method: 'GET',
      endpoint: '',
      query: {},
      path: {},
      headers: {},
      body: {},
    },
    trigger: {
      type: 'step_enter_and_dependency_change',
      depends_on: [],
      dependency_mode: 'any',
      trigger_fields: [],
      debounce_ms: 300,
      execute_when_inactive: false,
    },
    response: {
      type: field.type === 'dynamic_group'
        ? 'fields'
        : ['select', 'multi_select', 'radio', 'checkbox_group'].includes(field.type)
          ? 'options'
          : 'value',
      path: '$',
      value: 'value',
      label: 'label',
      field_key: 'key',
      field_label: 'label',
      field_type: 'type',
      field_placeholder: 'placeholder',
      field_help_text: 'help_text',
      field_validations: 'validation',
      default_field_type: 'text',
    },
  };
}

export function DataSourceEditor({ field, allKeys, value, onChange }: { field: FormField; allKeys: { key: string; label: string; type: FieldType }[]; value: DataSource; onChange: (v?: DataSource) => void }) {
  const choice = ['select', 'multi_select', 'radio', 'checkbox_group'].includes(field.type);
  const dynamicGroup = field.type === 'dynamic_group';
  useEffect(() => {
    if (dynamicGroup && value.response.type !== 'fields') {
      onChange({ ...value, response: { ...value.response, type: 'fields' } });
    } else if (!dynamicGroup && choice && value.response.type !== 'options') {
      onChange({ ...value, response: { ...value.response, type: 'options' } });
    } else if (!dynamicGroup && !choice && ['options', 'fields'].includes(value.response.type)) {
      onChange({ ...value, response: { ...value.response, type: 'value' } });
    }
  }, [choice, dynamicGroup, value.response.type]);
  const patch = (p: Partial<DataSource>) => onChange({ ...value, ...p });
  const request = value.request;
  const patchRequest = (p: Partial<DataSource['request']>) => patch({ request: { ...request, ...p } });
  const addParam = (bucket: 'query' | 'path' | 'headers' | 'body') => {
    const current = request[bucket];
    let n = 'param'; let i = 1;
    while (current[n]) n = `param_${i++}`;
    patchRequest({ [bucket]: { ...current, [n]: { source: 'field', field: allKeys[0]?.key || '' } } } as Partial<DataSource['request']>);
  };
  const updateParam = (bucket: 'query' | 'path' | 'headers' | 'body', key: string, param: DataSourceParam) => patchRequest({ [bucket]: { ...request[bucket], [key]: param } } as Partial<DataSource['request']>);
  const removeParam = (bucket: 'query' | 'path' | 'headers' | 'body', key: string) => { const next = { ...request[bucket] }; delete next[key]; patchRequest({ [bucket]: next } as Partial<DataSource['request']>); };
  const renameParam = (bucket: 'query' | 'path' | 'headers' | 'body', from: string, to: string) => {
    const nextName = to.trim();
    if (!nextName || nextName === from || request[bucket][nextName]) return;
    const next: Record<string, DataSourceParam> = {};
    Object.entries(request[bucket]).forEach(([key, param]) => {
      next[key === from ? nextName : key] = param;
    });
    patchRequest({ [bucket]: next } as Partial<DataSource['request']>);
  };
  return <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50/60 p-3">
    <div className="grid grid-cols-[100px_1fr] gap-2">
      <Field label="Method"><select className={inputClass()} value={request.method} onChange={(e) => patchRequest({ method: e.target.value as DataSource['request']['method'] })}>{['GET','POST','PUT','PATCH','DELETE'].map((m) => <option key={m}>{m}</option>)}</select></Field>
      <Field label="Endpoint"><input className={inputClass()} value={request.endpoint} placeholder="/api/v1/models" onChange={(e) => patchRequest({ endpoint: e.target.value })} /></Field>
    </div>
    <div className="grid grid-cols-2 gap-2">
      <Field label="Trigger">
        <select className={inputClass()} value={value.trigger.type} onChange={(e) => patch({ trigger: { ...value.trigger, type: e.target.value as DataSource['trigger']['type'] } })}>
          <option value="step_enter">When this step is entered</option><option value="dependency_change">When dependencies change</option><option value="step_enter_and_dependency_change">When step is entered or dependencies change</option><option value="form_load">Form load</option><option value="manual">Manual</option>
        </select>
      </Field>
      <Field label="Debounce (ms)"><input className={inputClass()} type="number" min={0} max={5000} value={value.trigger.debounce_ms} onChange={(e) => patch({ trigger: { ...value.trigger, debounce_ms: Number(e.target.value || 0) } })} /></Field>
    </div>
    <label className="flex items-start gap-2 rounded-lg border border-slate-200 bg-white p-2.5">
      <input
        type="checkbox"
        className="mt-0.5"
        checked={value.trigger.execute_when_inactive === true}
        onChange={(e) => patch({ trigger: { ...value.trigger, execute_when_inactive: e.target.checked } })}
      />
      <span>
        <span className="block text-[11px] font-medium text-slate-700">Run API while field is hidden or disabled</span>
        <span className="mt-0.5 block text-[10px] leading-4 text-slate-400">Off by default. When off, the API runs only after this field becomes visible and enabled.</span>
      </span>
    </label>
    {['dependency_change', 'step_enter_and_dependency_change'].includes(value.trigger.type) && (
      <>
        <Field label="Dependency rule">
          <select
            className={inputClass()}
            value={value.trigger.dependency_mode || 'any'}
            onChange={(e) => {
              const mode = e.target.value as DataSource['trigger']['dependency_mode'];
              const current = value.trigger.trigger_fields || [];
              patch({ trigger: { ...value.trigger, dependency_mode: mode, trigger_fields: mode === 'selected' ? current.filter((key) => allKeys.some((item) => item.key === key && item.key !== field.key)) : current } });
            }}
          >
            <option value="any">Any dependency changes</option>
            <option value="all">All visible dependencies change</option>
            <option value="selected">Selected dependencies change</option>
          </select>
          <p className={helpClass}>Hidden dependencies never block a request. “All” evaluates only dependencies currently visible in the form.</p>
        </Field>
        <div>
          <span className={labelClass}>Dependencies</span>
          <p className="mt-0.5 text-[10px] leading-4 text-slate-400">Fields whose values may be used by this API source. Select the full dependency set here; request parameters can independently choose which values are sent.</p>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {allKeys.filter((k) => k.key !== field.key).map((k) => (
              <label key={k.key} className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2 py-1 text-[10px] text-slate-600">
                <input
                  type="checkbox"
                  checked={value.trigger.depends_on.includes(k.key)}
                  onChange={(e) => {
                    const next = e.target.checked
                      ? Array.from(new Set(value.trigger.depends_on.concat(k.key)))
                      : value.trigger.depends_on.filter((x) => x !== k.key);
                    const selected = (value.trigger.trigger_fields || []).filter((x) => next.includes(x));
                    patch({ trigger: { ...value.trigger, depends_on: next, trigger_fields: selected } });
                  }}
                />
                {k.label || k.key} <span className="font-mono text-slate-400">({k.key})</span>
              </label>
            ))}
          </div>
        </div>
        {value.trigger.dependency_mode === 'selected' && (
          <Field label="Trigger fields">
            <MultiSelect
              options={value.trigger.depends_on
                .filter((key) => key !== field.key)
                .map((key) => {
                  const ref = allKeys.find((item) => item.key === key);
                  return { value: key, label: `${ref?.label || key} · ${key}` };
                })}
              value={value.trigger.trigger_fields || []}
              placeholder="Select fields..."
              onChange={(selected) => {
                patch({
                  trigger: {
                    ...value.trigger,
                    trigger_fields: selected,
                    depends_on: Array.from(
                      new Set([...value.trigger.depends_on, ...selected])
                    ),
                  },
                });
              }}
            />
            <p className={helpClass}>Only these selected fields trigger the API. If a selected field is hidden, changes from the visible selected fields can still trigger the API.</p>
          </Field>
        )}
      </>
    )}
    <div className="rounded-lg border border-slate-200 bg-white p-2.5">
      <div className="mb-1 text-[11px] font-semibold text-slate-700">Conditional parameters</div>
      <p className="text-[10px] leading-4 text-slate-400">Field parameters are sent only when their source field is currently visible by default. Turn this off only when a hidden field is intentionally required.</p>
    </div>
    <ParamGroup title="Query parameters" bucket="query" params={request.query} allKeys={allKeys} onAdd={() => addParam('query')} onChange={(k, v) => updateParam('query', k, v)} onRename={(from, to) => renameParam('query', from, to)} onRemove={(k) => removeParam('query', k)} />
    <ParamGroup title="Path parameters" bucket="path" params={request.path} allKeys={allKeys} onAdd={() => addParam('path')} onChange={(k, v) => updateParam('path', k, v)} onRename={(from, to) => renameParam('path', from, to)} onRemove={(k) => removeParam('path', k)} />
    {request.method !== 'GET' && <ParamGroup title="Request body" bucket="body" params={request.body} allKeys={allKeys} onAdd={() => addParam('body')} onChange={(k, v) => updateParam('body', k, v)} onRename={(from, to) => renameParam('body', from, to)} onRemove={(k) => removeParam('body', k)} />}
    <div className="grid grid-cols-2 gap-2">
      <Field label="Response type"><select className={inputClass()} value={value.response.type} onChange={(e) => patch({ response: { ...value.response, type: e.target.value as DataSource['response']['type'] } })}><option value="value">Single value</option><option value="options" disabled={!choice}>Options</option><option value="raw">Raw response</option><option value="fields" disabled={!dynamicGroup}>Dynamic fields</option></select></Field>
      <Field label="Response path"><input className={inputClass()} value={value.response.path} placeholder="$.data" onChange={(e) => patch({ response: { ...value.response, path: e.target.value } })} /></Field>
    </div>
    {value.response.type === 'options' && <div className="grid grid-cols-2 gap-2"><Field label="Option value key"><input className={inputClass()} value={value.response.value} onChange={(e) => patch({ response: { ...value.response, value: e.target.value } })} /></Field><Field label="Option label key"><input className={inputClass()} value={value.response.label} onChange={(e) => patch({ response: { ...value.response, label: e.target.value } })} /></Field></div>}
    {value.response.type === 'fields' && <div className="space-y-2 rounded-lg border border-primary-100 bg-primary-50/40 p-3"><p className="text-[11px] font-semibold text-slate-700">Map API rows to generated fields</p><div className="grid grid-cols-2 gap-2"><Field label="Field key property"><input className={inputClass()} value={value.response.field_key || 'key'} onChange={(e) => patch({ response: { ...value.response, field_key: e.target.value } })} /></Field><Field label="Label property"><input className={inputClass()} value={value.response.field_label || 'label'} onChange={(e) => patch({ response: { ...value.response, field_label: e.target.value } })} /></Field><Field label="Type property"><input className={inputClass()} value={value.response.field_type || 'type'} onChange={(e) => patch({ response: { ...value.response, field_type: e.target.value } })} /></Field><Field label="Default field type"><select className={inputClass()} value={value.response.default_field_type || 'text'} onChange={(e) => patch({ response: { ...value.response, default_field_type: e.target.value as DataSource['response']['default_field_type'] } })}>{['text','number','email','password','url','tel','date','time','datetime','textarea','select','multi_select','radio','checkbox','checkbox_group','file','hidden'].map((t) => <option key={t} value={t}>{t}</option>)}</select></Field><Field label="Placeholder property"><input className={inputClass()} value={value.response.field_placeholder || 'placeholder'} onChange={(e) => patch({ response: { ...value.response, field_placeholder: e.target.value } })} /></Field><Field label="Help text property"><input className={inputClass()} value={value.response.field_help_text || 'help_text'} onChange={(e) => patch({ response: { ...value.response, field_help_text: e.target.value } })} /></Field><Field label="Validation rules property"><input className={inputClass()} value={value.response.field_validations || 'validation'} onChange={(e) => patch({ response: { ...value.response, field_validations: e.target.value } })} /></Field></div><p className={helpClass}>Each API row becomes a runtime field. Validation rules from the response are applied to that field. If a row has no type, the default type above is used.</p></div>}
    <div className="flex items-center justify-between border-t border-slate-200 pt-2"><p className="text-[10px] leading-4 text-slate-500">Parameters can come from fields, static values, or approved user context. Endpoints must be relative application paths.</p><button type="button" className={dangerIconButton} title="Remove API data source" aria-label="Remove API data source" onClick={() => onChange(undefined)}><Trash2 className="h-3.5 w-3.5" /></button></div>
  </div>;
}

const FORM_SENSOR_TYPE = 'form_sensor_type';
/** The form-level sensor type is exposed by the backend as context key "sensor_type". */
const isFormSensorType = (param: DataSourceParam) => param.source === 'context' && param.key === 'sensor_type';
const FORM_CATEGORY = 'form_category';
/** The form-level category is exposed by the backend as context key "category". */
const isFormCategory = (param: DataSourceParam) => param.source === 'context' && param.key === 'category';

function ParamGroup({ title, bucket, params, allKeys, onAdd, onChange, onRename, onRemove }: {
  title: string;
  bucket: string;
  params: Record<string, DataSourceParam>;
  allKeys: { key: string; label: string; type: FieldType }[];
  onAdd: () => void;
  onChange: (key: string, value: DataSourceParam) => void;
  onRename: (from: string, to: string) => void;
  onRemove: (key: string) => void;
}) {
  const entries = Object.entries(params);
  return <div className="rounded-lg border border-slate-200 bg-white p-2.5">
    <div className="mb-2 flex items-center justify-between">
      <span className="text-[11px] font-semibold text-slate-700">{title}</span>
      <button type="button" className={subtleButton} onClick={onAdd}><Plus className="h-3 w-3" /> Add</button>
    </div>
    {entries.length === 0 ? <p className="text-[10px] text-slate-400">No parameters.</p> : <div className="space-y-1.5">
      {entries.map(([key, param], index) => {
        return <div key={`${bucket}-${index}`} className="grid grid-cols-[1fr_92px_1fr_28px] gap-1.5">
          <input
            className={inputClass()}
            value={key}
            aria-label={`Parameter name ${key}`}
            placeholder="Parameter name"
            onChange={(e) => onRename(key, e.target.value)}
          />
          <select className={inputClass()} value={isFormSensorType(param) ? FORM_SENSOR_TYPE : isFormCategory(param) ? FORM_CATEGORY : param.source} onChange={(e) => onChange(key, e.target.value === FORM_SENSOR_TYPE
            ? { source: 'context', key: 'sensor_type' }
            : e.target.value === FORM_CATEGORY
              ? { source: 'context', key: 'category' }
              : { ...param, source: e.target.value as DataSourceParam['source'], ...(isFormSensorType(param) || isFormCategory(param) ? { key: undefined } : {}) })}>
            <option value="field">Field</option><option value="static">Static</option><option value={FORM_SENSOR_TYPE}>Form sensor type</option><option value={FORM_CATEGORY}>Form category</option><option value="context">Context</option>
          </select>
          {param.source === 'field'
            ? <div className="min-w-0">
                <select className={inputClass()} value={param.field || ''} onChange={(e) => onChange(key, { ...param, field: e.target.value })}>{allKeys.map((f) => <option key={f.key} value={f.key}>{f.label || f.key} · {f.key}</option>)}</select>
                <label className="mt-1 flex items-center gap-1.5 text-[9px] text-slate-500">
                  <input type="checkbox" checked={param.only_if_visible !== false} onChange={(e) => onChange(key, { ...param, only_if_visible: e.target.checked })} />
                  Only when visible
                </label>
              </div>
            : isFormSensorType(param)
              ? <p className="self-center text-[10px] leading-4 text-slate-500">Sensor type chosen when this form was created.</p>
              : isFormCategory(param)
              ? <p className="self-center text-[10px] leading-4 text-slate-500">Category chosen when this form was created.</p>
              : param.source === 'context'
              ? <input className={inputClass()} value={param.key || ''} placeholder="user_id" onChange={(e) => onChange(key, { ...param, key: e.target.value })} />
              : <input className={inputClass()} value={param.value == null ? '' : String(param.value)} placeholder="Value" onChange={(e) => onChange(key, { ...param, value: e.target.value })} />}
          <button type="button" className={dangerIconButton} aria-label={`Remove ${bucket} parameter ${key}`} onClick={() => onRemove(key)}><Trash2 className="h-3.5 w-3.5" /></button>
        </div>;
      })}
      <p className="text-[10px] leading-4 text-slate-400">The first column is the actual request parameter name, e.g. <span className="font-mono">some_name</span> in <span className="font-mono">?some_name=...</span>.</p>
    </div>}
  </div>;
}
